import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useScrollLock } from '../../../../../hooks/useScrollLock';
import { FormProvider, useForm } from 'react-hook-form';
import { useAtom } from 'jotai';
import {
  Text,
  Spinner,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Button as ChakraButton,
  Textarea,
  Divider,
  Box,
  Switch,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb
} from '@chakra-ui/react';
import { articlePayloadAtom } from '../../../../CreateArticle/atom';
import { sidebarCollapsedAtom } from '../../../plugins/TableOfContentsPlugin/atom';
import { InputHookForm } from '../../../../Common/Form/input-with-hook-form';
import { TextAreaHookForm } from '../../../../Common/Form/textarea-hook-form';
import FormInputCounter from '../../../../Common/Form/InputCountCharacter/form-input-counter.component';
import DropzoneTwygsComponent from '../../../../Common/DropzoneTwygsComponent';
import EditArticleThumbnail from '../../../../CreateArticle/components/EditArticleThumbnail';
import HashtagInput from '../../../../Common/Form/textarea-custom-hook-form/textarea-hashtag.component';
import { TextAreaCustomContributor } from '../../../../Common/Form/textarea-custom-hook-form/textarea-contributor.component';
import { MenuHookForm } from '../../../../Common/Form/menu-with-hook-form';
import {
  ALLOWED_FILE_TYPES_THUMBNAIL,
  MAX_FILE_SIZE_10,
  getContentType,
  handleFileNameExtension
} from '../../../../../constants/image.constant';
import {
  AI_SUMMARY_SUPPORT,
  INVESTMENT_POOL_TOOLTIP,
  REVENUE_BREAKDOWN_TOOLTIP
} from '../../../../../constants/twygs.constant';
import {
  postAssetUpload,
  processThumbnail,
  fetchListWrittingType
} from '../../../../../apis/article.api';
import { fetchListTopic } from '../../../../../apis/discovery.api';
import { FieldErrors } from '../../hooks/usePublishArticle';
import axios from 'axios';
import { TooltipComponent } from '../../../../Tooltip';
import Label from '../../../../Common/Form/label';

// Icons
import WandStarWhite from '../../../../../assets/wand-stars-white.svg';
import WandStarBlack from '../../../../../assets/wand-stars-black.svg';
import InformIcon from '../../../../../assets/inform-gray-icon.svg';
import InformWarningIcon from '../../../../../assets/inform-warning.svg';
import EyeIcon from '../../../../../assets/eye-icon.svg';
import EditSquareIcon from '../../../../../assets/edit-square-icon.svg';
import ArrowRightIcon from '../../../../../assets/arrow-right-white-icon.svg';
import InvestIcon from '../../../../../assets/invest-icon.svg';

// Thumbnail upload constants
const THUMBNAIL_MAX_DIMENSIONS = { width: 800, height: 400 };
const THUMBNAIL_SUPPORTED_FORMATS = ['PNG', 'JPG', 'JPEG'];

// Collapse Icons
const ChevronDownIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

// Settings/Gear Icon
const SettingsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#4CAF50">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

// Investment Pool Icon
const InvestmentIcon = () => <img src={InvestIcon} alt="" className="w-5 h-5" />;

// Post Details Icon
const PostDetailsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#4CAF50">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

// Sidebar Toggle Icon
const SidebarCollapseIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#4CAF50">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
    />
  </svg>
);

const SidebarExpandIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#4CAF50">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 5l7 7-7 7M5 5l7 7-7 7"
    />
  </svg>
);

// AI Generation Button Component
const AIGenerateButton: React.FC<{
  onClick: () => void;
  isLoading: boolean;
  disabled: boolean;
  label: string;
  size?: 'sm' | 'md';
  hasEnoughContent: boolean;
  className?: string;
}> = ({ onClick, isLoading, disabled, label, size = 'sm', hasEnoughContent, className }) => (
  <Tooltip
    placement="top"
    bg="transparent"
    color="black"
    rounded="8px"
    padding="0"
    label={
      <Box
        p="12px"
        bg="white"
        borderRadius="8px"
        border="2px solid transparent"
        backgroundClip="padding-box, border-box"
        backgroundImage="linear-gradient(white, white), linear-gradient(270deg, #a855f7, #4CAF50)"
        w={{ base: '100%', md: 'fit-content' }}
        whiteSpace={{ base: 'normal', md: 'nowrap' }}
      >
        {disabled
          ? 'Write some content first to generate with AI'
          : `Generate ${label.toLowerCase()} based on your article's content`}
      </Box>
    }
  >
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`
        flex justify-center items-center px-4 py-2 rounded-lg text-sm font-[900] transition-all duration-200 min-w-[165px]
        ${
          disabled || isLoading
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
            : 'bg-gradient-to-r from-purple-500 to-growth-green-500 text-white hover:from-purple-600 hover:to-growth-green-900 hover:shadow-md'
        }
        ${size === 'md' ? 'px-3 py-2 text-sm min-w-full' : ''}
        ${className}
      `}
    >
      <div className="flex items-center gap-1">
        <span className="whitespace-nowrap">{isLoading ? 'Generating...' : label}</span>
        {isLoading ? (
          <Spinner size="xs" color={hasEnoughContent ? 'white' : 'gray.400'} />
        ) : (
          <img src={hasEnoughContent ? WandStarWhite : WandStarBlack} alt="" className="w-4 h-4" />
        )}
      </div>
    </button>
  </Tooltip>
);

// Collapsible Section Component
const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  accentColor?: string;
}> = ({ title, icon, isOpen, onToggle, children, accentColor = 'growth-green' }) => (
  <div
    className={`border-b border-gray-100 last:border-b-0 ${isOpen ? 'flex-1 flex flex-col' : ''}`}
  >
    <button
      onClick={onToggle}
      className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors`}
    >
      <div className="flex items-center gap-2">
        <span className={`text-${accentColor}-500`}>{icon}</span>
        <span className={`font-semibold text-sm text-${accentColor}-500`}>{title}</span>
      </div>
      <span
        className={`text-${accentColor}-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      >
        <ChevronDownIcon />
      </span>
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isOpen ? 'flex-1 opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <div className="bg-[#edf7ee] h-full">{children}</div>
    </div>
  </div>
);

interface ArticleEditorSidebarProps {
  articleContent: string;
  forceSave?: () => void;
  triggerSave?: () => void;
  onPreview?: () => void;
  onPublish?: () => void;
  onSaveDraft?: () => void;
  isPublishing?: boolean;
  isSavingDraft?: boolean;
  isEdit?: boolean;
  saveStatus?: string;
  lastSaved?: Date | null;
  methods: ReturnType<typeof useForm>;
  fieldErrors?: FieldErrors;
}

const ArticleEditorSidebar: React.FC<ArticleEditorSidebarProps> = ({
  articleContent = '',
  forceSave,
  triggerSave,
  // onPreview,
  onPublish,
  onSaveDraft,
  isPublishing = false,
  isSavingDraft = false,
  isEdit = false,
  saveStatus,
  lastSaved,
  methods,
  fieldErrors = {}
}) => {
  // Lexical editor context for preview toggle
  const [editor] = useLexicalComposerContext();
  const [isEditable, setIsEditable] = useState(true);

  // Listen to editor editable state changes
  useEffect(() => {
    return editor.registerEditableListener((editable) => {
      setIsEditable(editable);
    });
  }, [editor]);

  // Toggle preview mode
  const handleTogglePreview = useCallback(() => {
    editor.setEditable(!editor.isEditable());
  }, [editor]);

  const [articlePayload, setArticlePayload] = useAtom(articlePayloadAtom);

  // Sidebar collapse state (shared with editor via atom)
  const [isCollapsed, setIsCollapsed] = useAtom(sidebarCollapsedAtom);

  // Mobile sidebar open state
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Scroll lock to prevent scroll propagation to parent
  const scrollLockRef = useScrollLock();
  const mobileScrollLockRef = useScrollLock();

  // Section collapse states
  const [openSections, setOpenSections] = useState({
    postDetails: true,
    additionalSettings: false,
    investmentPool: false
  });

  // Thumbnail states
  const [fileImg, setFileImg] = useState(articlePayload?.cover || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const [originalFileBlob, setOriginalFileBlob] = useState<Blob | null>(null);
  const [openEditArticleThumbnail, setOpenEditArticleThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string>('');
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [showProcessingSuccess, setShowProcessingSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lastCrop, setLastCrop] = useState<{ x: number; y: number } | null>(null);
  const [lastZoom, setLastZoom] = useState<number | null>(null);
  const [lastCroppedAreaPixels, setLastCroppedAreaPixels] = useState<any>(null);

  // AI Generation Loading States
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);

  // Thumbnail prompt states
  const [thumbnailPrompt, setThumbnailPrompt] = useState('');
  const [isPromptPopoverOpen, setIsPromptPopoverOpen] = useState(false);

  // Data lists
  const [listTopic, setListTopic] = useState([]);
  const [listWritingType, setListWritingType] = useState([]);

  // Investment pool state
  const [investmentPoolSize, setInvestmentPoolSize] = useState(
    articlePayload?.sharingPoolPercentage || 5
  );
  const pendingInvestmentSaveRef = useRef(false);
  const snapPoints = [5, 25, 50, 75, 90];
  const pendingAISummariesSaveRef = useRef(false);

  // Check if there's enough content for AI generation
  const hasEnoughContent = articleContent && articleContent.trim().length > 100;

  // Sync fieldErrors from validation with thumbnailError state
  useEffect(() => {
    if (fieldErrors.title) {
      methods.setError('title', { type: 'manual', message: fieldErrors.title });
    }
    if (fieldErrors.cover) {
      setThumbnailError(fieldErrors.cover);
    }
  }, [fieldErrors]);

  // Fetch topics and writing types
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [topicsRes, writingTypesRes] = await Promise.all([
          fetchListTopic(),
          fetchListWrittingType()
        ]);
        if (topicsRes) setListTopic(topicsRes);
        if (writingTypesRes) setListWritingType(writingTypesRes);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Sync form values and article payload (including writing type)
  useEffect(() => {
    const subscription = methods.watch((values, { name }) => {
      setArticlePayload((prev) => ({
        ...prev,
        title: values.title || '',
        description: values.description || '',
        isEnableAISummaries: values.isEnableAISummaries || false,
        writingTypeId: values.writingTypeId ?? prev.writingTypeId,
        hashtags: values.hashtags || [],
        topicIds: values.topicIds || []
      }));

      if (name === 'isEnableAISummaries') {
        pendingAISummariesSaveRef.current = true;
      }

      if (name === 'writingTypeId') {
        const selectedId = values.writingTypeId;
        const typeObj = listWritingType.find((t: any) => t.id === selectedId) || null;
        setArticlePayload((prev) => ({
          ...prev,
          writingTypeId: selectedId ?? null,
          writingType: typeObj ? typeObj.name : ''
        }));
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, listWritingType, setArticlePayload]);

  // Sync investment pool size
  useEffect(() => {
    if (articlePayload?.sharingPoolPercentage !== undefined) {
      setInvestmentPoolSize(articlePayload.sharingPoolPercentage);
    }
  }, [articlePayload?.sharingPoolPercentage]);

  // Sync cover image
  useEffect(() => {
    if (articlePayload?.cover) {
      setFileImg(articlePayload.cover);
    }
  }, [articlePayload?.cover]);

  // Auto-save triggers
  useEffect(() => {
    if (pendingInvestmentSaveRef.current && forceSave && triggerSave) {
      pendingInvestmentSaveRef.current = false;
      triggerSave();
      forceSave();
    }
  }, [articlePayload?.sharingPoolPercentage, forceSave, triggerSave]);

  useEffect(() => {
    if (pendingAISummariesSaveRef.current && forceSave && triggerSave) {
      pendingAISummariesSaveRef.current = false;
      triggerSave();
      forceSave();
    }
  }, [articlePayload?.isEnableAISummaries, forceSave, triggerSave]);

  // Toggle section
  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections({
      postDetails: section === 'postDetails' ? !openSections.postDetails : false,
      additionalSettings:
        section === 'additionalSettings' ? !openSections.additionalSettings : false,
      investmentPool: section === 'investmentPool' ? !openSections.investmentPool : false
    });
  };

  // File validation
  const validateFiles = (file: File) => {
    let messageError = '';
    if (!ALLOWED_FILE_TYPES_THUMBNAIL.includes(file?.type)) {
      messageError = 'Please ensure your file type is PNG, JPG or JPEG';
    }
    if (file?.size > MAX_FILE_SIZE_10) {
      messageError = 'Please ensure your file size is less than 10MB';
    }
    return messageError;
  };

  // Upload thumbnail to S3
  const uploadThumbnailToS3 = async (croppedBlob: Blob): Promise<{ url: string; key: string }> => {
    try {
      const timestamp = Date.now();
      const filename = handleFileNameExtension(`thumbnail-${timestamp}.jpg`, 'image/jpeg');

      const contentKeys = await postAssetUpload({ fileName: [filename] });
      const presignedUrl = contentKeys[0].url;
      const s3Key = contentKeys[0].key;

      await axios.put(presignedUrl, croppedBlob, {
        headers: {
          'content-disposition': 'inline',
          'Content-Type': getContentType(filename.toLowerCase()),
          'x-amz-acl': 'public-read'
        }
      });

      const previewUrl = URL.createObjectURL(croppedBlob);
      return { url: previewUrl, key: s3Key };
    } catch (error) {
      console.error('Error uploading thumbnail to S3:', error);
      throw new Error('Failed to upload thumbnail');
    }
  };

  // Process thumbnail with backend
  const processThumbnailWithBackend = async (file: File) => {
    try {
      setIsUploadingThumbnail(true);
      setThumbnailError('');

      const timestamp = Date.now();
      const filename = handleFileNameExtension(`original-${timestamp}.jpg`, file.type);
      const contentKeys = await postAssetUpload({ fileName: [filename] });
      const presignedUrl = contentKeys[0].url;
      const s3Key = contentKeys[0].key;

      await axios.put(presignedUrl, file, {
        headers: {
          'content-disposition': 'inline',
          'Content-Type': getContentType(filename.toLowerCase()),
          'x-amz-acl': 'public-read'
        }
      });

      const result = await processThumbnail(
        JSON.stringify({ s3Key: s3Key, articleId: articlePayload?.id })
      );

      if (result.success && result.thumbnails) {
        const previewUrl = result.thumbnails.medium;
        setOriginalFileBlob(file);
        setFileImg(previewUrl);
        setSelectedFile(file);
        setThumbnailError('');

        setArticlePayload({
          ...articlePayload,
          cover: previewUrl,
          coverCloudKey: result.thumbnails.keys.large,
          thumbnails: result.thumbnails.keys
        });

        setShowProcessingSuccess(true);
        setTimeout(() => setShowProcessingSuccess(false), 3000);
      } else {
        throw new Error(result.message || 'Failed to process thumbnail');
      }
    } catch (error) {
      console.error('Error processing thumbnail with backend:', error);
      setThumbnailError('Failed to process thumbnail. Please try again.');
      setSelectedFile(file);
      setOpenEditArticleThumbnail(true);
    } finally {
      setIsUploadingThumbnail(false);
    }
  };

  const handleUploadImage = (files: File[]) => {
    setFileImg('');
    setThumbnailError('');
    setOriginalFileBlob(null);
    setShowProcessingSuccess(false);
    const newFile = files[0];
    const validationError = validateFiles(newFile);

    if (newFile && !validationError) {
      processThumbnailWithBackend(newFile);
    } else if (validationError) {
      setThumbnailError(validationError);
    }
  };

  const handleSaveCroppedImage = useCallback(
    async ({ previewUrl, croppedBlob, originalFile, crop, zoom, croppedAreaPixels }) => {
      try {
        setIsUploadingThumbnail(true);
        const { url: blobUrl, key: s3Key } = await uploadThumbnailToS3(croppedBlob);
        setIsUploadingThumbnail(false);

        setOriginalFileBlob(originalFile);
        setFileImg(blobUrl);
        setSelectedFile(croppedBlob);
        setThumbnailError('');
        setLastCrop(crop);
        setLastZoom(zoom);
        setLastCroppedAreaPixels(croppedAreaPixels);

        setArticlePayload({
          ...articlePayload,
          cover: blobUrl,
          coverCloudKey: s3Key
        });

        setOpenEditArticleThumbnail(false);
      } catch (error) {
        console.error('Error saving cropped image:', error);
        setThumbnailError('Failed to upload thumbnail. Please try again.');
        setOriginalFileBlob(originalFile);
        setFileImg(previewUrl);
        setSelectedFile(croppedBlob);
        setArticlePayload({ ...articlePayload, cover: previewUrl });
        setOpenEditArticleThumbnail(false);
      }
    },
    [articlePayload, setArticlePayload]
  );

  // Mobile drawer handlers (reusable versions of inline handlers)
  const handleTitleSave = useCallback(() => {
    const title = methods.getValues('title');
    if (title !== articlePayload?.title) {
      setArticlePayload({ ...articlePayload, title });
      triggerSave?.();
    }
  }, [methods, articlePayload, setArticlePayload, triggerSave]);

  const handleDescriptionSave = useCallback(() => {
    const description = methods.getValues('description');
    if (description !== articlePayload?.description) {
      setArticlePayload({ ...articlePayload, description });
      triggerSave?.();
    }
  }, [methods, articlePayload, setArticlePayload, triggerSave]);

  const handleRemoveThumbnail = useCallback(() => {
    setFileImg('');
    setSelectedFile(null);
    setOriginalFileBlob(null);
    setArticlePayload({ ...articlePayload, cover: '', coverCloudKey: '' });
    triggerSave?.();
  }, [articlePayload, setArticlePayload, triggerSave]);

  const handleFileDrop = useCallback(
    (files: File[]) => {
      handleUploadImage(files);
    },
    [handleUploadImage]
  );

  const handleAISummariesToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      methods.setValue('isEnableAISummaries', isChecked);
      setArticlePayload({ ...articlePayload, isEnableAISummaries: isChecked });
      pendingAISummariesSaveRef.current = true;
    },
    [methods, articlePayload, setArticlePayload]
  );

  // AI Generation Handlers
  const handleGenerateTitle = async () => {
    if (!hasEnoughContent) return;
    setIsGeneratingTitle(true);
    try {
      const response = await fetch('/api/v1/publisher/ai/content-generation/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articleContent,
          type: 'title',
          currentTitle: methods.getValues('title'),
          currentDescription: methods.getValues('description')
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      if (result.success && result.title) {
        methods.setValue('title', result.title.title);
        setArticlePayload({ ...articlePayload, title: result.title.title });
      }
    } catch (error) {
      console.error('Error generating title:', error);
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (!hasEnoughContent) return;
    setIsGeneratingDescription(true);
    try {
      const response = await fetch('/api/v1/publisher/ai/content-generation/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articleContent,
          type: 'description',
          currentTitle: methods.getValues('title'),
          currentDescription: methods.getValues('description')
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      if (result.success && result.description) {
        methods.setValue('description', result.description.description);
        setArticlePayload({ ...articlePayload, description: result.description.description });
      }
    } catch (error) {
      console.error('Error generating description:', error);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleGenerateThumbnail = async (customPrompt?: string) => {
    if (!hasEnoughContent) return;
    setIsGeneratingThumbnail(true);
    try {
      const response = await fetch('/api/v1/publisher/ai/content-generation/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articleContent,
          type: 'thumbnail',
          currentTitle: methods.getValues('title'),
          currentDescription: methods.getValues('description'),
          customPrompt: customPrompt || undefined
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const generativeResult = await response.json();

      if (generativeResult.success && generativeResult.thumbnail) {
        try {
          const imageResponse = await fetch(generativeResult.thumbnail.thumbnailUrl);
          const imageBlob = await imageResponse.blob();
          const { key: s3Key } = await uploadThumbnailToS3(imageBlob);

          // Step 2: Process with backend ArticleThumbnailService
          setIsUploadingThumbnail(true);
          const result = await processThumbnail(
            JSON.stringify({
              s3Key: s3Key,
              articleId: articlePayload?.id
            })
          );

          if (result.success && result.thumbnails) {
            const previewUrl = result.thumbnails.medium;

            setFileImg(previewUrl);
            setSelectedFile(imageBlob);
            setOriginalFileBlob(imageBlob);
            setArticlePayload({
              ...articlePayload,
              cover: previewUrl,
              coverCloudKey: result.thumbnails.keys.large,
              thumbnails: result.thumbnails.keys
            });
            setShowProcessingSuccess(true);
            setTimeout(() => setShowProcessingSuccess(false), 3000);
          } else {
            throw new Error(result.message || 'Failed to process thumbnail');
          }
        } catch (uploadError) {
          console.error('Error uploading AI-generated thumbnail:', uploadError);
          setFileImg(generativeResult.thumbnail.thumbnailUrl);
          setOriginalFileBlob(null);
          setArticlePayload({ ...articlePayload, cover: generativeResult.thumbnail.thumbnailUrl });
        }
      }
    } catch (error) {
      console.error('Error generating thumbnail:', error);
    } finally {
      setIsUploadingThumbnail(false);
      setIsGeneratingThumbnail(false);
    }
  };

  const handleGenerateAll = async () => {
    if (!hasEnoughContent) return;
    setIsGeneratingAll(true);
    try {
      const response = await fetch('/api/v1/publisher/ai/content-generation/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articleContent,
          type: 'all',
          currentTitle: methods.getValues('title'),
          currentDescription: methods.getValues('description')
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const resultGenerateAll = await response.json();

      if (resultGenerateAll.success) {
        let updatedPayload = { ...articlePayload };

        if (resultGenerateAll.title) {
          methods.setValue('title', resultGenerateAll.title.title);
          updatedPayload.title = resultGenerateAll.title.title;
        }
        if (resultGenerateAll.description) {
          methods.setValue('description', resultGenerateAll.description.description);
          updatedPayload.description = resultGenerateAll.description.description;
        }
        if (resultGenerateAll.thumbnail) {
          try {
            const imageResponse = await fetch(resultGenerateAll.thumbnail.thumbnailUrl);
            const imageBlob = await imageResponse.blob();
            const { url: blobUrl, key: s3Key } = await uploadThumbnailToS3(imageBlob);

            // Step 2: Process with backend ArticleThumbnailService
            setIsUploadingThumbnail(true);
            const result = await processThumbnail(
              JSON.stringify({
                s3Key: s3Key,
                articleId: articlePayload?.id
              })
            );

            if (result.success && result.thumbnails) {
              // Use the medium size for preview (already has proper aspect ratio)
              const previewUrl = result.thumbnails.medium;

              setFileImg(blobUrl);
              setSelectedFile(imageBlob);
              setOriginalFileBlob(imageBlob);

              updatedPayload = {
                ...updatedPayload,
                cover: previewUrl,
                coverCloudKey: result.thumbnails.keys.large,
                thumbnails: result.thumbnails.keys
              };
              // Show success message
              setShowProcessingSuccess(true);
              setTimeout(() => setShowProcessingSuccess(false), 3000);
            }
          } catch (uploadError) {
            setFileImg(resultGenerateAll.thumbnail.thumbnailUrl);
            setOriginalFileBlob(null);
            updatedPayload = { ...updatedPayload, cover: resultGenerateAll.thumbnail.thumbnailUrl };
          }
        }
        setArticlePayload(updatedPayload);
      }
    } catch (error) {
      console.error('Error generating all content:', error);
    } finally {
      setIsGeneratingAll(false);
      setIsUploadingThumbnail(false);
    }
  };

  // Investment Pool Handlers
  // Updates local state only (for smooth UI while sliding)
  const handleSliderChange = (value: number) => {
    setInvestmentPoolSize(value);
  };

  // Saves when done sliding or clicking snap points
  const handleInvestmentPoolChangeEnd = (value: number) => {
    setInvestmentPoolSize(value);
    setArticlePayload({ ...articlePayload, sharingPoolPercentage: value });
    // Trigger save directly instead of relying on useEffect
    if (triggerSave && forceSave) {
      triggerSave();
      setTimeout(() => forceSave(), 100);
    }
  };

  const yourFinalPortion = 100 - investmentPoolSize - 10; // 10% platform fee

  // Helper function to render mobile components (shared between collapsed and expanded states)
  const renderMobileComponents = () => (
    <>
      {/* Mobile Bottom Action Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-30 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={handleTogglePreview}
            className={`flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-full transition-colors ${
              isEditable
                ? 'text-gray-700 border border-gray-300 hover:bg-gray-50'
                : 'text-growth-green-700 bg-growth-green-50 border border-growth-green-200'
            }`}
          >
            <img src={isEditable ? EyeIcon : EditSquareIcon} alt="" className="w-4 h-4" />
            {isEditable ? 'Preview' : 'Edit'}
          </button>

          <button
            onClick={() => setIsMobileOpen(true)}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-growth-green-700 bg-growth-green-50 border border-growth-green-200 rounded-full hover:bg-growth-green-100 transition-colors"
          >
            <SettingsIcon />
            Settings
          </button>

          <button
            onClick={onPublish}
            disabled={isPublishing}
            className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-growth-green-500 rounded-full hover:bg-growth-green-600 transition-colors disabled:opacity-50"
          >
            {isPublishing ? 'Publishing...' : 'Publish'}
            {!isPublishing && <img src={ArrowRightIcon} alt="" className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* Save Status for Mobile */}
        {saveStatus === 'saving' && (
          <p className="text-[10px] text-gray-400 text-center mt-1.5 flex items-center justify-center gap-1">
            <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Saving...
          </p>
        )}
        {lastSaved && saveStatus === 'saved' && (
          <p className="text-[10px] text-gray-400 text-center mt-1.5">
            ✓ Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
        {saveStatus === 'error' && (
          <p className="text-[10px] text-red-500 text-center mt-1.5">
            ⚠ Save failed -{' '}
            <button onClick={onSaveDraft} className="underline">
              Retry
            </button>
          </p>
        )}
      </div>

      {/* Mobile Sidebar Drawer */}
      {isMobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity"
            onClick={() => setIsMobileOpen(false)}
          />

          {/* Drawer */}
          <div className="md:hidden fixed inset-y-0 right-0 w-full max-w-[400px] bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right">
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
              <h2 className="font-bold text-lg text-gray-900">Post Settings</h2>
              <button
                onClick={() => setIsMobileOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Drawer Content */}
            <div
              ref={mobileScrollLockRef}
              className="flex-1 overflow-y-auto no-scrollbar flex flex-col"
            >
              <FormProvider {...methods}>
                {/* Post Details Section */}
                <CollapsibleSection
                  title="Post Details"
                  icon={<PostDetailsIcon />}
                  isOpen={openSections.postDetails}
                  onToggle={() => toggleSection('postDetails')}
                >
                  {/* AI Assistant Box */}
                  <div className="px-4 py-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <img src={WandStarBlack} alt="" className="w-4 h-4" />
                        <span className="font-semibold text-sm text-gray-800">AI Assistant</span>
                      </div>
                      <div className="ml-4">
                        <AIGenerateButton
                          onClick={handleGenerateAll}
                          isLoading={isGeneratingAll}
                          disabled={!hasEnoughContent}
                          label="Generate All Fields"
                          size="md"
                          hasEnoughContent={hasEnoughContent}
                        />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Generate thumbnail, title, and description automatically based on your content
                    </p>
                    {!hasEnoughContent && (
                      <p className="text-sm text-amber-600 flex items-center gap-1 mb-2">
                        <img src={InformWarningIcon} alt="" className="w-4 h-4" />
                        Write some content first to unlock AI features
                      </p>
                    )}
                  </div>

                  <Divider borderColor="#D9D9D9" my={1} />

                  {/* Post Title */}
                  <div className="px-4 py-2" id="article-metadata-header-mobile">
                    <div className="flex items-center justify-between mb-2">
                      <Label>Post Title</Label>
                      <AIGenerateButton
                        onClick={handleGenerateTitle}
                        isLoading={isGeneratingTitle}
                        disabled={!hasEnoughContent}
                        label="Generate Title"
                        hasEnoughContent={hasEnoughContent}
                      />
                    </div>
                    <FormInputCounter
                      name="title"
                      placeholder="Name your article"
                      label=""
                      maxLength={100}
                      component={InputHookForm}
                      onBlur={handleTitleSave}
                    />
                  </div>

                  <Divider borderColor="#D9D9D9" my={1} />

                  {/* Post Thumbnail */}
                  <div className="px-4 py-2">
                    <div className="flex items-center justify-between mb-2">
                      <Label>Post Thumbnail</Label>
                      <Popover
                        isOpen={isPromptPopoverOpen}
                        onClose={() => setIsPromptPopoverOpen(false)}
                        placement="top-end"
                      >
                        <PopoverTrigger>
                          <div>
                            <AIGenerateButton
                              onClick={() => hasEnoughContent && setIsPromptPopoverOpen(true)}
                              isLoading={isGeneratingThumbnail}
                              disabled={!hasEnoughContent}
                              label="Generate Thumbnail"
                              hasEnoughContent={hasEnoughContent}
                            />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0" borderRadius="12px" boxShadow="lg">
                          <PopoverArrow />
                          <PopoverCloseButton />
                          <PopoverHeader
                            fontWeight="bold"
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            py={3}
                          >
                            Generate AI Thumbnail
                          </PopoverHeader>
                          <PopoverBody p={4}>
                            <Text fontSize="sm" color="gray.600" mb={3}>
                              Describe your ideal thumbnail or leave empty for auto-generation
                            </Text>
                            <Textarea
                              value={thumbnailPrompt}
                              onChange={(e) => setThumbnailPrompt(e.target.value)}
                              placeholder="e.g., A vibrant cityscape at sunset..."
                              size="sm"
                              mb={3}
                              rows={3}
                            />
                            <ChakraButton
                              size="sm"
                              width="full"
                              colorScheme="green"
                              onClick={() => {
                                handleGenerateThumbnail(thumbnailPrompt);
                                setIsPromptPopoverOpen(false);
                              }}
                              isLoading={isGeneratingThumbnail}
                            >
                              Generate
                            </ChakraButton>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Thumbnail Upload Area */}
                    {fileImg || articlePayload?.cover ? (
                      <div className="relative group">
                        <img
                          src={fileImg || articlePayload?.cover}
                          alt="Thumbnail"
                          className="w-full h-40 object-cover rounded-lg border border-gray-200"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="px-3 py-1.5 bg-white text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-100"
                          >
                            Replace
                          </button>
                          <button
                            onClick={handleRemoveThumbnail}
                            className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <DropzoneTwygsComponent
                        validateFiles={handleFileDrop}
                        isLoading={isUploadingThumbnail}
                        acceptFileTypes={ALLOWED_FILE_TYPES_THUMBNAIL.join(',')}
                        isSingle
                        name="cover"
                        minH="120px"
                        maxDimensions={THUMBNAIL_MAX_DIMENSIONS}
                        supportedFormats={THUMBNAIL_SUPPORTED_FORMATS}
                        error={thumbnailError}
                        isMobile={true}
                        title={
                          <p className="!text-02-dim-gray text-sm text-center">
                            <span className="!text-growth-green-500 font-[900]">
                              Click to upload file
                            </span>{' '}
                            or drag and drop
                          </p>
                        }
                        desc={
                          <p className="text-sm text-gray-400 mt-1">
                            Supported file types: {THUMBNAIL_SUPPORTED_FORMATS.join(', ')}
                            <br />
                            {`(max. ${THUMBNAIL_MAX_DIMENSIONS.width}x${THUMBNAIL_MAX_DIMENSIONS.height}px , 1MB)`}
                          </p>
                        }
                      />
                    )}
                    {thumbnailError && (
                      <p className="text-sm text-red-500 mt-1">{thumbnailError}</p>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ALLOWED_FILE_TYPES_THUMBNAIL.join(',')}
                      onChange={(e) => e.target.files?.[0] && handleFileDrop([e.target.files[0]])}
                      className="hidden"
                    />
                  </div>

                  <Divider borderColor="#D9D9D9" my={1} />

                  {/* Post Description */}
                  <div className="px-4 py-2">
                    <div className="flex items-center justify-between mb-2">
                      <Label optional>Post Description</Label>
                      <AIGenerateButton
                        onClick={handleGenerateDescription}
                        isLoading={isGeneratingDescription}
                        disabled={!hasEnoughContent}
                        label="Generate Description"
                        hasEnoughContent={hasEnoughContent}
                      />
                    </div>
                    <FormInputCounter
                      name="description"
                      placeholder="Write description"
                      label=""
                      isOptional
                      maxLength={2000}
                      component={TextAreaHookForm}
                      onBlur={handleDescriptionSave}
                    />
                  </div>

                  <Divider borderColor="#D9D9D9" my={1} />

                  {/* Categories */}
                  <div className="flex flex-col px-4 py-2 gap-2">
                    <Label optional>Categories</Label>
                    <MenuHookForm
                      name="topicIds"
                      isOptional
                      isShowCheckBox
                      placeholder="Select categories"
                      data={listTopic || []}
                      defaultValue={articlePayload?.topicIds}
                    />
                  </div>
                </CollapsibleSection>

                {/* Additional Settings Section */}
                <CollapsibleSection
                  title="Additional Settings"
                  icon={<SettingsIcon />}
                  isOpen={openSections.additionalSettings}
                  onToggle={() => toggleSection('additionalSettings')}
                >
                  {/* Post Type */}
                  <div className="flex flex-col gap-2 px-4 py-2">
                    <Label optional>Post Type</Label>
                    <MenuHookForm
                      name="writingTypeId"
                      isOptional
                      placeholder="Select post type"
                      data={listWritingType || []}
                      nameDisplay="name"
                      defaultValue={
                        articlePayload?.writingTypeId
                          ? listWritingType.find(
                              (t: any) => t.id === articlePayload.writingTypeId
                            ) || null
                          : null
                      }
                    />
                  </div>

                  <Divider borderColor="#D9D9D9" my={1} />

                  {/* Tags */}
                  <div className="flex flex-col gap-2 px-4 py-2">
                    <Label optional>Tags</Label>
                    <HashtagInput
                      name="hashtags"
                      isOptional
                      placeholder="Press enter after each tag"
                    />
                  </div>

                  <Divider borderColor="#D9D9D9" my={1} />

                  {/* AI Summaries Toggle */}
                  <div className="flex items-center justify-between px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Label>
                        <div className="flex flex-row gap-2">
                          <span>Enable AI Summaries</span>
                          <TooltipComponent
                            maxW="300px"
                            placement="top"
                            Icon={InformIcon}
                            className="w-4 h-4"
                            description={AI_SUMMARY_SUPPORT}
                          />
                        </div>
                      </Label>
                    </div>
                    <Switch
                      isChecked={articlePayload?.isEnableAISummaries}
                      onChange={handleAISummariesToggle}
                      colorScheme="green"
                      size="md"
                    />
                  </div>

                  <Divider borderColor="#D9D9D9" my={1} />

                  {/* Contributors */}
                  <div className="flex flex-col gap-2 px-4 py-2">
                    <Label optional>Contributors</Label>
                    <TextAreaCustomContributor
                      p={{ base: '8px 12px' }}
                      minHeight="48px"
                      name="contributors"
                      isOptional
                      placeholder="Add names of contributors"
                      isHiddenBottomContent
                      css={{ '&::-webkit-scrollbar': { display: 'none' } }}
                    />
                  </div>
                </CollapsibleSection>

                {/* Investment Pool Settings Section */}
                <CollapsibleSection
                  title="Investment Pool Settings"
                  icon={<InvestmentIcon />}
                  isOpen={openSections.investmentPool}
                  onToggle={() => toggleSection('investmentPool')}
                >
                  <div className="space-y-4">
                    {/* Investment Pool Slider */}
                    <div className="px-4 py-2">
                      <div className="flex items-center justify-between mb-3">
                        <Label>
                          <div className="flex flex-row gap-2">
                            <span className="font-bold text-sm">Investment Pool</span>
                            <TooltipComponent
                              maxW="300px"
                              placement="top"
                              Icon={InformIcon}
                              className="w-4 h-4"
                              description={INVESTMENT_POOL_TOOLTIP}
                            />
                            <img src={InvestIcon} alt="" className="w-5 h-5" />
                          </div>
                        </Label>
                        <span className="text-growth-green-500 font-bold text-sm">
                          {investmentPoolSize}%
                        </span>
                      </div>

                      <Slider
                        aria-label="investment-pool"
                        min={5}
                        max={90}
                        value={investmentPoolSize}
                        onChange={handleSliderChange}
                        onChangeEnd={handleInvestmentPoolChangeEnd}
                        isDisabled={articlePayload.isPublished}
                        colorScheme="green"
                      >
                        <SliderTrack h="6px" bg="gray.200" borderRadius="full">
                          <SliderFilledTrack
                            h="6px"
                            bg="linear-gradient(90deg, #C8E6C9 1.02%, #4CAF50 55.95%, #204A22 91.59%)"
                          />
                        </SliderTrack>
                        <SliderThumb border="1px" borderColor="#204A22" boxSize={4} />
                      </Slider>

                      <div className="flex justify-between items-center mt-1">
                        {snapPoints.map((milestone) => (
                          <p
                            key={milestone}
                            className={`text-sm font-semibold transition-colors duration-200 cursor-pointer hover:text-growth-green-500 ${
                              investmentPoolSize === milestone
                                ? 'text-growth-green-500 font-bold'
                                : 'text-gray-400'
                            }`}
                            onClick={() =>
                              !articlePayload.isPublished &&
                              handleInvestmentPoolChangeEnd(milestone)
                            }
                          >
                            {milestone}%
                          </p>
                        ))}
                      </div>

                      {/* Your Final Portion */}
                      <div className="mt-6 flex items-center justify-between">
                        <span className="text-sm font-bold text-gray-500">Your final portion:</span>
                        <span className="text-sm font-semibold">
                          {yourFinalPortion}% of total ad revenue
                        </span>
                      </div>
                    </div>

                    <Divider borderColor="#D9D9D9" my={1} />

                    {/* Revenue Breakdown */}
                    <div className="flex flex-col gap-2 px-4 py-2">
                      <Label>
                        <div className="flex flex-row gap-2">
                          <span className="font-bold text-sm">Revenue Breakdown</span>
                          <TooltipComponent
                            maxW="300px"
                            placement="top"
                            Icon={InformIcon}
                            className="w-4 h-4"
                            description={REVENUE_BREAKDOWN_TOOLTIP}
                          />
                        </div>
                      </Label>
                      <Box className="flex h-6 w-full" transition="all 0.3s ease-in-out">
                        <Box
                          className="bg-growth-green-500 text-center text-white py-1 font-bold text-sm rounded-l-full whitespace-nowrap flex items-center justify-center"
                          width={`${yourFinalPortion}%`}
                        >
                          {yourFinalPortion > 12 && `${yourFinalPortion}%`}
                        </Box>
                        <Box
                          className={`bg-[#012BC2] text-center text-white py-1 font-bold text-sm ${investmentPoolSize === 90 ? 'rounded-l-full' : ''} whitespace-nowrap flex items-center justify-center`}
                          width={`${investmentPoolSize}%`}
                        >
                          {investmentPoolSize > 12 && `${investmentPoolSize}%`}
                        </Box>
                        <Box
                          className="bg-02-dim-gray text-center text-white py-1 font-bold text-sm rounded-r-full flex items-center justify-center"
                          width="10%"
                        ></Box>
                      </Box>

                      {/* Legend */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-1">
                          <div className="rounded-full bg-growth-green-500 w-2.5 h-2.5"></div>
                          <span className="font-medium">Your Earnings</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="rounded-full bg-[#012BC2] w-2.5 h-2.5"></div>
                          <span className="font-medium">Investment Pool</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="rounded-full bg-02-dim-gray w-2.5 h-2.5"></div>
                          <span className="font-medium">Platform</span>
                        </div>
                      </div>
                    </div>

                    {articlePayload.isPublished && (
                      <p className="text-sm text-amber-600 font-medium text-center">
                        Investment pool is locked after publishing
                      </p>
                    )}
                  </div>
                </CollapsibleSection>
              </FormProvider>
            </div>

            {/* Drawer Footer Actions */}
            <div className="border-t border-gray-200 px-4 py-3 bg-white">
              <div className="flex items-center gap-2">
                {!isEdit && (
                  <button
                    onClick={onSaveDraft}
                    disabled={
                      isSavingDraft || articlePayload.isPublished || saveStatus === 'saving'
                    }
                    className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                      saveStatus === 'error'
                        ? 'text-red-600 border border-red-300 bg-red-50'
                        : saveStatus === 'saved'
                          ? 'text-growth-green-600 border border-growth-green-200 bg-growth-green-50 cursor-default'
                          : 'text-gray-700 border border-gray-300'
                    } disabled:cursor-not-allowed`}
                  >
                    {saveStatus === 'saving' || isSavingDraft ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Saving...
                      </>
                    ) : saveStatus === 'error' ? (
                      'Retry save'
                    ) : saveStatus === 'saved' ? (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Saved
                      </>
                    ) : (
                      'Save draft'
                    )}
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsMobileOpen(false);
                    onPublish?.();
                  }}
                  disabled={isPublishing}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-growth-green-500 rounded-full hover:bg-growth-green-600 transition-colors disabled:opacity-50"
                >
                  {isPublishing ? 'Publishing...' : 'Publish'}
                  {!isPublishing && <img src={ArrowRightIcon} alt="" className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Thumbnail Modal */}
      {openEditArticleThumbnail && selectedFile && (
        <EditArticleThumbnail
          isOpen={openEditArticleThumbnail}
          onClose={() => setOpenEditArticleThumbnail(false)}
          file={selectedFile}
          onSaveCroppedImage={handleSaveCroppedImage}
          initialCrop={lastCrop}
          initialZoom={lastZoom}
          initialCroppedAreaPixels={lastCroppedAreaPixels}
        />
      )}
    </>
  );

  // Collapsed sidebar view
  if (isCollapsed) {
    return (
      <>
        {/* Desktop collapsed sidebar */}
        <div className="hidden md:flex flex-col items-center w-[52px] fixed right-0 top-[58px] h-[calc(100vh-58px)] bg-white border-l border-gray-200 py-4 z-20">
          <button
            onClick={() => setIsCollapsed(false)}
            className="p-2 hover:bg-gray-100 rounded-lg mb-4 text-gray-600"
            title="Expand sidebar"
          >
            <SidebarCollapseIcon />
          </button>
          <div className="flex flex-col gap-3 items-center">
            <button
              onClick={() => {
                setIsCollapsed(false);
                setOpenSections((s) => ({ ...s, postDetails: true }));
              }}
              className="p-2 hover:bg-growth-green-50 rounded-lg text-growth-green-600"
              title="Post Details"
            >
              <PostDetailsIcon />
            </button>
            <button
              onClick={() => {
                setIsCollapsed(false);
                setOpenSections((s) => ({ ...s, additionalSettings: true }));
              }}
              className="p-2 hover:bg-growth-green-50 rounded-lg text-growth-green-600"
              title="Additional Settings"
            >
              <SettingsIcon />
            </button>
            <button
              onClick={() => {
                setIsCollapsed(false);
                setOpenSections((s) => ({ ...s, investmentPool: true }));
              }}
              className="p-2 hover:bg-growth-green-50 rounded-lg text-growth-green-600"
              title="Investment Pool"
            >
              <InvestmentIcon />
            </button>
          </div>
        </div>

        {/* Mobile components (always rendered) */}
        {renderMobileComponents()}
      </>
    );
  }

  return (
    <>
      <div className="hidden md:flex flex-col w-[520px] fixed right-0 top-[58px] h-[calc(100vh-58px)] bg-white border-l border-gray-200 z-20 shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <button
            onClick={() => setIsCollapsed(true)}
            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"
            title="Collapse sidebar"
          >
            <SidebarExpandIcon />
          </button>
        </div>

        {/* Scrollable Content */}
        <div ref={scrollLockRef} className="flex-1 overflow-y-auto no-scrollbar flex flex-col">
          <FormProvider {...methods}>
            {/* Post Details Section */}
            <CollapsibleSection
              title="Post Details"
              icon={<PostDetailsIcon />}
              isOpen={openSections.postDetails}
              onToggle={() => toggleSection('postDetails')}
            >
              {/* AI Assistant Box */}
              <div className="px-4 py-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <img src={WandStarBlack} alt="" className="w-4 h-4" />
                    <span className="font-semibold text-sm text-gray-800">AI Assistant</span>
                  </div>
                  <div className="ml-4">
                    <AIGenerateButton
                      onClick={handleGenerateAll}
                      isLoading={isGeneratingAll}
                      disabled={!hasEnoughContent}
                      label="Generate All Fields"
                      size="md"
                      hasEnoughContent={hasEnoughContent}
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Generate thumbnail, title, and description automatically based on your content
                </p>
                {!hasEnoughContent && (
                  <div className="flex items-center gap-1.5 mb-3 text-amber-600">
                    <img src={InformWarningIcon} alt="" className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Write some content first to unlock AI features
                    </span>
                  </div>
                )}
              </div>

              <Divider borderColor="#D9D9D9" my={1} />

              {/* Post Title */}
              <div className="px-4 py-2" id="article-metadata-header">
                <div className="flex items-center justify-between mb-2">
                  <Label>Post Title</Label>
                  <AIGenerateButton
                    onClick={handleGenerateTitle}
                    isLoading={isGeneratingTitle || isGeneratingAll}
                    disabled={!hasEnoughContent}
                    label="Generate Title"
                    hasEnoughContent={hasEnoughContent}
                  />
                </div>
                <FormInputCounter
                  name="title"
                  placeholder="Name your article"
                  label=""
                  maxLength={100}
                  component={InputHookForm}
                />
              </div>

              <Divider borderColor="#D9D9D9" my={1} />

              {/* Post Thumbnail */}
              <div className="px-4 py-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Post Thumbnail</Label>
                  <Popover
                    isOpen={isPromptPopoverOpen}
                    onClose={() => setIsPromptPopoverOpen(false)}
                    placement="bottom-end"
                  >
                    <PopoverTrigger>
                      <div>
                        <AIGenerateButton
                          onClick={() => hasEnoughContent && setIsPromptPopoverOpen(true)}
                          isLoading={isGeneratingThumbnail || isGeneratingAll}
                          disabled={!hasEnoughContent}
                          label="Generate Thumbnail"
                          hasEnoughContent={hasEnoughContent}
                        />
                      </div>
                    </PopoverTrigger>
                    <PopoverContent maxW="300px">
                      <PopoverArrow />
                      <PopoverCloseButton />
                      <PopoverHeader>
                        <Text fontSize="sm" fontWeight="600">
                          AI Thumbnail Generation
                        </Text>
                      </PopoverHeader>
                      <PopoverBody>
                        <div className="space-y-3">
                          <ChakraButton
                            w="full"
                            size="sm"
                            colorScheme="green"
                            onClick={() => {
                              setIsPromptPopoverOpen(false);
                              handleGenerateThumbnail();
                            }}
                          >
                            Auto-Generate
                          </ChakraButton>
                          <div>
                            <Text fontSize="xs" mb={1} color="gray.600">
                              Or use custom prompt:
                            </Text>
                            <Textarea
                              value={thumbnailPrompt}
                              onChange={(e) => setThumbnailPrompt(e.target.value)}
                              placeholder="e.g., modern office, vibrant colors..."
                              size="sm"
                              rows={2}
                              mb={2}
                            />
                            <ChakraButton
                              w="full"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setIsPromptPopoverOpen(false);
                                handleGenerateThumbnail(thumbnailPrompt);
                                setThumbnailPrompt('');
                              }}
                              isDisabled={!thumbnailPrompt.trim()}
                            >
                              Use Custom Prompt
                            </ChakraButton>
                          </div>
                        </div>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </div>

                <p className="text-sm text-gray-500 mb-2">
                  This image will be used as the article thumbnail, displayed in listings and feeds.
                  It will also appear at the top of your article.
                </p>

                {isUploadingThumbnail && (
                  <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-700 text-sm">
                      Processing thumbnail...
                    </div>
                  </div>
                )}

                {showProcessingSuccess && (
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700 text-sm">
                      <span>✓</span> Thumbnail processed successfully!
                    </div>
                  </div>
                )}

                {fileImg ? (
                  <div className="relative">
                    <div className="relative rounded-lg overflow-hidden border border-gray-300 bg-white cursor-pointer group">
                      <img
                        src={fileImg}
                        alt="Article thumbnail"
                        className="w-full aspect-video object-cover transition-transform duration-200 group-hover:scale-105"
                        onClick={() => {
                          if (originalFileBlob) {
                            const file = new File([originalFileBlob], 'current-thumbnail.jpg', {
                              type: originalFileBlob.type || 'image/jpeg'
                            });
                            setSelectedFile(file);
                            setOpenEditArticleThumbnail(true);
                          }
                        }}
                      />
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                          className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-full text-sm shadow-lg"
                          title="Change image"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFileImg('');
                            setSelectedFile(null);
                            setOriginalFileBlob(null);
                            setArticlePayload({ ...articlePayload, cover: '', coverCloudKey: '' });
                          }}
                          className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full text-sm shadow-lg"
                          title="Remove image"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) {
                          handleUploadImage(Array.from(files));
                        }
                      }}
                      name="cover"
                      className="hidden"
                    />
                  </div>
                ) : (
                  <DropzoneTwygsComponent
                    validateFiles={handleFileDrop}
                    onFileUpload={handleUploadImage}
                    maxSize={1}
                    maxDimensions={THUMBNAIL_MAX_DIMENSIONS}
                    error={thumbnailError}
                    acceptFileTypes={ALLOWED_FILE_TYPES_THUMBNAIL.join(',')}
                    isLoading={isUploadingThumbnail}
                    supportedFormats={THUMBNAIL_SUPPORTED_FORMATS}
                    title={
                      <p className="!text-02-dim-gray text-sm">
                        <span className="!text-growth-green-500 font-[900]">
                          Click to upload file
                        </span>{' '}
                        or drag and drop
                      </p>
                    }
                    desc={
                      <p className="text-sm text-gray-400 mt-1">
                        Supported file types: {THUMBNAIL_SUPPORTED_FORMATS.join(', ')}
                        <br />
                        {`(max. ${THUMBNAIL_MAX_DIMENSIONS.width}x${THUMBNAIL_MAX_DIMENSIONS.height}px , 1MB)`}
                      </p>
                    }
                  />
                )}
              </div>

              <Divider borderColor="#D9D9D9" my={1} />

              {/* Post Description */}
              <div className="px-4 py-2">
                <div className="flex items-center justify-between mb-2">
                  <Label optional>Post Description</Label>
                  <AIGenerateButton
                    onClick={handleGenerateDescription}
                    isLoading={isGeneratingDescription || isGeneratingAll}
                    disabled={!hasEnoughContent}
                    label="Generate Description"
                    hasEnoughContent={hasEnoughContent}
                  />
                </div>
                <FormInputCounter
                  component={TextAreaHookForm}
                  css={{ '&::-webkit-scrollbar': { display: 'none' } }}
                  name="description"
                  placeholder="Write description"
                  label=""
                  isOptional
                  minHeight="100px"
                  maxLength={2000}
                />
              </div>

              <Divider borderColor="#D9D9D9" my={1} />

              {/* Categories */}
              <div className="flex flex-col px-4 py-2 gap-2">
                <Label optional>Categories</Label>
                <MenuHookForm
                  name="topicIds"
                  isOptional
                  isShowCheckBox
                  placeholder="Select categories"
                  data={listTopic || []}
                  defaultValue={articlePayload?.topicIds}
                />
              </div>
            </CollapsibleSection>

            {/* Additional Settings Section */}
            <CollapsibleSection
              title="Additional Settings"
              icon={<SettingsIcon />}
              isOpen={openSections.additionalSettings}
              onToggle={() => toggleSection('additionalSettings')}
            >
              {/* Post Type */}
              <div className="px-4 py-2">
                <Label optional>Post Type</Label>
                <MenuHookForm
                  name="writingTypeId"
                  isOptional
                  placeholder="Select post type"
                  data={listWritingType || []}
                  nameDisplay="name"
                  defaultValue={
                    articlePayload?.writingTypeId
                      ? listWritingType.find((t: any) => t.id === articlePayload.writingTypeId) ||
                        null
                      : null
                  }
                />
              </div>

              <Divider borderColor="#D9D9D9" my={1} />

              {/* Tags */}
              <div className="flex flex-col gap-2 px-4 py-2">
                <Label optional>Tags</Label>
                <HashtagInput name="hashtags" isOptional placeholder="Press enter after each tag" />
              </div>

              <Divider borderColor="#D9D9D9" my={1} />

              {/* Enable AI Summaries */}
              <div className="flex items-center justify-between px-4 py-2">
                <Label>
                  <div className="flex flex-row gap-2">
                    <span>Enable AI Summaries</span>
                    <TooltipComponent
                      maxW="300px"
                      placement="top"
                      Icon={InformIcon}
                      className="w-4 h-4"
                      description={AI_SUMMARY_SUPPORT}
                    />
                  </div>
                </Label>
                <Switch
                  isChecked={articlePayload?.isEnableAISummaries || false}
                  variant="light-green"
                  size="md"
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    methods.setValue('isEnableAISummaries', isChecked);
                    setArticlePayload({ ...articlePayload, isEnableAISummaries: isChecked });
                    pendingAISummariesSaveRef.current = true;
                  }}
                />
              </div>

              <Divider borderColor="#D9D9D9" my={1} />

              {/* Contributors */}
              <div className="flex flex-col gap-2 px-4 py-2">
                <Label optional>Contributors</Label>
                <TextAreaCustomContributor
                  p={{ base: '8px 12px' }}
                  minHeight={'48px'}
                  name="contributors"
                  isOptional
                  placeholder="Add names of contributors"
                  isHiddenBottomContent
                  css={{ '&::-webkit-scrollbar': { display: 'none' } }}
                />
              </div>
            </CollapsibleSection>

            {/* Investment Pool Settings Section */}
            <CollapsibleSection
              title="Investment Pool Settings"
              icon={<InvestmentIcon />}
              isOpen={openSections.investmentPool}
              onToggle={() => toggleSection('investmentPool')}
            >
              <div className="space-y-4">
                {/* Slider Card */}
                <div className="p-3 rounded-lg flex flex-col gap-2 px-4 py-2">
                  <div className="flex justify-between items-center">
                    <Label>
                      <div className="flex flex-row gap-2">
                        <span className="font-bold text-sm">Investment Pool</span>
                        <TooltipComponent
                          maxW="300px"
                          placement="top"
                          Icon={InformIcon}
                          className="w-4 h-4"
                          description={INVESTMENT_POOL_TOOLTIP}
                        />
                        <img src={InvestIcon} alt="" className="w-5 h-5" />
                      </div>
                    </Label>
                    <span className="text-growth-green-500 text-sm font-bold">
                      {investmentPoolSize}%
                    </span>
                  </div>

                  <Slider
                    aria-label="investment-pool-slider"
                    min={5}
                    max={90}
                    value={investmentPoolSize}
                    onChange={handleSliderChange}
                    onChangeEnd={handleInvestmentPoolChangeEnd}
                    isDisabled={articlePayload.isPublished}
                    colorScheme="green"
                  >
                    <SliderTrack h="6px" bg="gray-200" borderRadius="full">
                      <SliderFilledTrack
                        h="6px"
                        bg="linear-gradient(90deg, #C8E6C9 1.02%, #4CAF50 55.95%, #204A22 91.59%)"
                      />
                    </SliderTrack>
                    <SliderThumb border="1px" borderColor="#204A22" boxSize={4} />
                  </Slider>

                  <div className="flex justify-between items-center">
                    {snapPoints.map((milestone) => (
                      <p
                        key={milestone}
                        className={`text-sm font-semibold transition-colors duration-200 cursor-pointer hover:text-growth-green-500 ${
                          investmentPoolSize === milestone
                            ? 'text-growth-green-500 font-bold'
                            : 'text-gray-400'
                        }`}
                        onClick={() =>
                          !articlePayload.isPublished && handleInvestmentPoolChangeEnd(milestone)
                        }
                      >
                        {milestone}%
                      </p>
                    ))}
                  </div>
                </div>

                {/* Your Final Portion */}
                <div className="mt-6 flex items-center justify-between px-4 py-2">
                  <span className="text-sm font-bold text-gray-500">Your final portion:</span>
                  <span className="text-sm font-semibold">
                    {yourFinalPortion}% of total ad revenue
                  </span>
                </div>

                <Divider borderColor="#D9D9D9" my={1} />

                {/* Revenue Breakdown */}
                <div className="flex flex-col gap-2 px-4 py-2">
                  <Label>
                    <div className="flex flex-row gap-2">
                      <span className="font-bold text-sm">Revenue Breakdown</span>
                      <TooltipComponent
                        maxW="300px"
                        placement="top"
                        Icon={InformIcon}
                        className="w-4 h-4"
                        description={REVENUE_BREAKDOWN_TOOLTIP}
                      />
                    </div>
                  </Label>

                  <Box className="flex h-6 w-full" transition="all 0.3s ease-in-out">
                    <Box
                      className="bg-growth-green-500 text-center text-white py-1 font-bold text-sm rounded-l-full whitespace-nowrap flex items-center justify-center"
                      width={`${yourFinalPortion}%`}
                    >
                      {yourFinalPortion > 12 && `${yourFinalPortion}%`}
                    </Box>
                    <Box
                      className={`bg-[#012BC2] text-center text-white py-1 font-bold text-sm ${investmentPoolSize === 90 ? 'rounded-l-full' : ''} whitespace-nowrap flex items-center justify-center`}
                      width={`${investmentPoolSize}%`}
                    >
                      {investmentPoolSize > 12 && `${investmentPoolSize}%`}
                    </Box>
                    <Box
                      className="bg-02-dim-gray text-center text-white py-1 font-bold text-sm rounded-r-full flex items-center justify-center"
                      width="10%"
                    ></Box>
                  </Box>

                  {/* Legend */}
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-1">
                      <div className="rounded-full bg-growth-green-500 w-2.5 h-2.5"></div>
                      <span className="font-medium">Your Earnings</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="rounded-full bg-[#012BC2] w-2.5 h-2.5"></div>
                      <span className="font-medium">Investment Pool</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="rounded-full bg-02-dim-gray w-2.5 h-2.5"></div>
                      <span className="font-medium">Platform</span>
                    </div>
                  </div>
                </div>

                {articlePayload.isPublished && (
                  <p className="text-sm text-amber-600 font-medium text-center">
                    Investment pool is locked after publishing
                  </p>
                )}
              </div>
            </CollapsibleSection>
          </FormProvider>
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="border-t border-gray-200 px-4 py-3 bg-white">
          <div className="flex items-center gap-2">
            <button
              onClick={handleTogglePreview}
              className={`flex items-center justify-center gap-1.5 px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                isEditable
                  ? 'text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                  : 'text-growth-green-700 bg-growth-green-50 border border-growth-green-200 hover:bg-growth-green-100'
              }`}
            >
              <img src={isEditable ? EyeIcon : EditSquareIcon} alt="" className="w-3.5 h-3.5" />
              {isEditable ? 'Preview' : 'Edit'}
            </button>
            {!isEdit && (
              <button
                onClick={onSaveDraft}
                disabled={isSavingDraft || articlePayload.isPublished || saveStatus === 'saving'}
                className={`flex items-center justify-center gap-1.5 px-4 py-1.5 text-sm font-semibold rounded-full transition-all ${
                  saveStatus === 'error'
                    ? 'text-red-600 border border-red-300 bg-red-50 hover:bg-red-100 hover:border-red-400'
                    : saveStatus === 'saved'
                      ? 'text-growth-green-600 border border-growth-green-200 bg-growth-green-50 cursor-default'
                      : saveStatus === 'saving' || isSavingDraft
                        ? 'text-gray-500 border border-gray-200 bg-gray-50 cursor-wait'
                        : 'text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                } disabled:cursor-not-allowed`}
              >
                {saveStatus === 'saving' || isSavingDraft ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </>
                ) : saveStatus === 'error' ? (
                  <>
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    Retry save
                  </>
                ) : saveStatus === 'saved' ? (
                  <>
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Saved
                  </>
                ) : (
                  'Save draft'
                )}
              </button>
            )}
            <button
              onClick={onPublish}
              disabled={isPublishing}
              className="flex items-center justify-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-growth-green-500 border border-growth-green-500 rounded-full hover:bg-growth-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
            >
              {isPublishing ? 'Publishing...' : 'Publish'}
              {!isPublishing && <img src={ArrowRightIcon} alt="" className="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* Last Saved Timestamp */}
          {lastSaved && saveStatus === 'saved' && (
            <p className="text-[10px] text-gray-400 text-center mt-1.5">
              Last saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      </div>

      {/* Mobile components (always rendered) */}
      {renderMobileComponents()}
    </>
  );
};

export default ArticleEditorSidebar;
